/**
 * The view toggle's geometry, in one place.
 *
 * The component draws from it, and the desktop frame needs the pill's width to
 * keep the lockup and the strip's links clear of it. Every number is measured
 * off the Sept 19 references in a 375 x 812 frame — the artboards' 1125 x 2436
 * points at 3x.
 *
 * `TOGGLE_SCALE` is the one line to change if that frame turns out to be
 * 390 x 844, as the Sept 16 phone cards read it: 390 / 375 redraws the whole
 * control about 4% larger. The cards beside it are drawn at the larger reading,
 * so the toggle lands about 4% smaller than they do. That is the frame, not a
 * bug.
 */

/** The frame the numbers below were measured in. */
export const TOGGLE_FRAME_W = 375;

/** Switch frames here. */
export const TOGGLE_SCALE = 1;

/** Which items the pill holds, which is what decides its width. */
export type TogglePill = 'signedIn' | 'signedOut' | 'braked';

export type ToggleItem = 'explore' | 'schedule' | 'account';

/**
 * Where a narrow desktop window falls back to, below 460px: even the smallest
 * lockup and a full-size pill cannot both clear the header. Sam's own mock of a
 * narrow window is pasted at about this scale.
 */
export const TOGGLE_NARROW_SCALE = 0.75;

const RAW = {
  height: 52.6,
  /** The highlight's inset from the pill's top, bottom and ends. */
  inset: 9.8,
  /** 52.6 - 2 x 9.8. */
  highlight: 33,
  /** Tiled between the insets; Explore and My Schedule are the bold label plus 2 x 15.5. */
  box: { explore: 86.5, schedule: 120.8, account: 33 },
  /** 9.8 + 86.5 + 120.8 + 33 + 9.8 = 259.9; signed out adds the Sign In remainder. */
  width: { signedIn: 260, signedOut: 282, braked: 227 },
  /** Sign In has no box: its right edge sits this far in from the pill's. */
  signInRight: 25.6,
  /**
   * What Sign In keeps between itself and the last box. The reference leaves it
   * nothing — the measured inset puts its S on the box's edge — and our Canela
   * Deck, three pixels wider, would put the S under the My Schedule highlight.
   * It gives up inset for this gap rather than the other way around.
   */
  signInGap: 10,
  icon: 22,
  label: { off: 14.1, on: 15 },
  /** Measured to the physical screen bottom, so a safe area replaces it rather than adding to it. */
  bottom: 18.4,
  /** What the band keeps clear above the pill. */
  bandGap: 12,
} as const;

export const TOGGLE_ITEMS: Record<TogglePill, ToggleItem[]> = {
  signedIn: ['explore', 'schedule', 'account'],
  signedOut: ['explore', 'schedule'],
  braked: ['explore', 'schedule'],
};

export const TOGGLE_LABELS: Record<ToggleItem, string> = {
  explore: 'Explore',
  schedule: 'My Schedule',
  account: 'Account',
};

/** A phone's pill sits on the page; a desktop's sits over it, and lets it through. */
export const TOGGLE_FILL = { phone: 'rgba(11,9,9,0.75)', desktop: 'rgba(11,9,9,0.40)' } as const;

export interface ToggleBox {
  key: ToggleItem;
  /** From the pill's left edge. */
  x: number;
  w: number;
}

export interface ToggleMetrics {
  scale: number;
  width: number;
  height: number;
  inset: number;
  highlight: number;
  boxes: ToggleBox[];
  /** Signed out, and not braked: the Sign In label, which is never highlighted. */
  signIn: boolean;
  signInRight: number;
  signInGap: number;
  icon: number;
  labelOff: number;
  labelOn: number;
}

/** The pill's width: what the desktop frame keeps the lockup and links clear of. */
export const toggleWidth = (pill: TogglePill, scale = 1) => RAW.width[pill] * TOGGLE_SCALE * scale;

export function toggleMetrics(pill: TogglePill, scale = 1): ToggleMetrics {
  const s = TOGGLE_SCALE * scale;
  const boxes: ToggleBox[] = [];
  let x = RAW.inset;
  for (const key of TOGGLE_ITEMS[pill]) {
    boxes.push({ key, x: x * s, w: RAW.box[key] * s });
    x += RAW.box[key];
  }
  return {
    scale: s,
    width: RAW.width[pill] * s,
    height: RAW.height * s,
    inset: RAW.inset * s,
    highlight: RAW.highlight * s,
    boxes,
    signIn: pill === 'signedOut',
    signInRight: RAW.signInRight * s,
    signInGap: RAW.signInGap * s,
    icon: RAW.icon * s,
    labelOff: RAW.label.off * s,
    labelOn: RAW.label.on * s,
  };
}

/** The phone pill's gap to the physical bottom of the screen. */
export const TOGGLE_BOTTOM = RAW.bottom * TOGGLE_SCALE;

/** `bottom` for the phone's fixed pill: the safe area replaces the measured gap. */
export const TOGGLE_BOTTOM_CSS = `max(${TOGGLE_BOTTOM}px, env(safe-area-inset-bottom, 0px))`;

/**
 * The strip along the bottom of a phone that belongs to the toggle: the pill,
 * its gap to the screen and 12px above it. Anything that would otherwise sit
 * under the toggle clears this.
 */
export const TOGGLE_BAND = `calc(${RAW.height * TOGGLE_SCALE}px + ${TOGGLE_BOTTOM_CSS} + ${RAW.bandGap}px)`;

/**
 * The band in pixels, resolved against this browser's own safe area — for the
 * few places that have to measure rather than write a length in CSS.
 */
export function toggleBandPx(): number {
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;left:-9999px;visibility:hidden;height:${TOGGLE_BAND}`;
  document.body.appendChild(probe);
  const height = probe.offsetHeight;
  probe.remove();
  return height;
}
